import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  TextField,
  Alert,
  Box,
  Chip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AppSwitch from "components/Shared/AppSwitch";
import AppForm from "components/Shared/AppForm";
import { useAuth } from "hooks";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import {
  useLazyGetDDLEntityListQuery,
  useDetailEntityQuery,
} from "store/services/entityApi";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import { useLazyGetDDLShipmentListForCODQuery } from "store/services/shipmentApi";
import { useListGlobalListQuery } from "store/services/globalListApi";
import { useLazyGetVendorCODRequestTicketQuery } from "store/services/ticketApi";
import { GLOBAL_LIST_CONSTANTS } from "data/static/globalListOptions";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import { useListListQuery } from "store/services/listService";
import { toast } from "react-toastify";

const INITIAL_VALUE = {
  account: null,
  entity: null,
  shipmentId: [],
  transferMode: "",
  bankName: "",
  actualAmount: "",
  feeAmount: "",
  transferAmount: "",
  receiver: "",
  vendor: null,
  notes: "",
  isActive: true,
  paymentAccount: null,
  ticket: null,
  deductChargeFromCOD: true,
};

const CodTransferForm = ({
  codTransferDetails = null,
  isBusy = false,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { isAccountAdmin, isSystemAdmin, viewEntity } = useAuth();
  const [vendorTicket, setVendorTicket] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      ...INITIAL_VALUE,
      account: viewEntity?.entity?.account || null,
      entity: viewEntity?.entity || null,
    },
  });

  const { data: bankNameOptions, isLoading: loadingBankNameOptions } =
    useListGlobalListQuery({
      type: GLOBAL_LIST_CONSTANTS.BANK_NAME,
      isSubType: false,
      includeChildren: true,
      status: "Active",
    });

  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();

  const [
    fetchVendors,
    {
      data: vendorList,
      isLoading: loadingVendors,
      isFetching: fetchingVendors,
    },
  ] = useLazyGetDdlVendorProfileQuery();

  const [
    fetchShipments,
    {
      data: shipmentList,
      isLoading: loadingShipments,
      isFetching: fetchingShipments,
    },
  ] = useLazyGetDDLShipmentListForCODQuery();

  const [
    fetchVendorTicket,
    { data: ticketData, isLoading: loadingTicket, isFetching: fetchingTicket },
  ] = useLazyGetVendorCODRequestTicketQuery();

  const { data: entityTypeList = [] } = useListListQuery(
    { type: ENTITY_LIST_CONSTANTS.PAYMENT_METHOD, status: "Active" },
    { skip: false, refetchOnMountOrArgChange: true },
  );

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchVendor = useWatch({ control, name: "vendor" });
  const _watchShipmentId = useWatch({ control, name: "shipmentId" });
  const _watchTransferMode = useWatch({ control, name: "transferMode" });
  const _watchDeductCharge = useWatch({ control, name: "deductChargeFromCOD" });

  const { data: entityDetailResponse } = useDetailEntityQuery(
    _watchEntity?._id,
    { skip: !_watchEntity?._id },
  );

  const serviceChargePercentage = useMemo(() => {
    const entityData = entityDetailResponse?.data || entityDetailResponse;
    const serviceCharge = entityData?.serviceCharge;
    if (serviceCharge?.isActive && serviceCharge?.serviceAmount) {
      return serviceCharge.serviceAmount;
    }
    return 0;
  }, [entityDetailResponse]);

  const _mappedAccountTypeList = useMemo(
    () =>
      Array.isArray(entityTypeList)
        ? entityTypeList.map((item) => ({
            label: item?.name || "",
            value: item?._id || "",
          }))
        : [],
    [entityTypeList],
  );

  const selectedCollectionMethod = useMemo(() => {
    if (!Array.isArray(entityTypeList) || !_watchTransferMode) return null;
    return entityTypeList.find((item) => item._id === _watchTransferMode);
  }, [_watchTransferMode, entityTypeList]);

  // Summary totals from selected shipments
  const totalCOD = useMemo(
    () =>
      (_watchShipmentId || []).reduce((sum, s) => sum + (s.codAmount || 0), 0),
    [_watchShipmentId],
  );

  const totalCharge = useMemo(
    () =>
      (_watchShipmentId || []).reduce(
        (sum, s) => sum + (s.chargeAmount || 0),
        0,
      ),
    [_watchShipmentId],
  );

  useEffect(() => {
    fetchVendors();
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    fetchVendors,
    fetchEntities,
    _watchAccount?._id,
    viewEntity?.entity?.account?._id,
    isAccountAdmin,
    isSystemAdmin,
  ]);

  useEffect(() => {
    if (_watchVendor?._id && _watchAccount?._id && _watchEntity?._id) {
      fetchVendorTicket({
        vendorId: _watchVendor._id,
        account: _watchAccount._id,
        entity: _watchEntity._id,
      });
    } else {
      setVendorTicket(null);
    }
  }, [
    _watchVendor?._id,
    _watchAccount?._id,
    _watchEntity?._id,
    fetchVendorTicket,
  ]);

  useEffect(() => {
    if (ticketData) {
      setVendorTicket(ticketData);
      setValue("ticket", ticketData._id);
    } else {
      setVendorTicket(null);
      setValue("ticket", null);
    }
  }, [ticketData, setValue]);

  useEffect(() => {
    if (_watchVendor?._id) {
      fetchShipments({ vendorId: _watchVendor._id });
    } else {
      setValue("shipmentId", []);
    }
  }, [_watchVendor?._id, fetchShipments, setValue]);

  useEffect(() => {
    if (!shipmentList || shipmentList.length === 0) return;
    if (codTransferDetails) {
      const validShipments = codTransferDetails.shipmentId?.filter((s) =>
        shipmentList.some((sl) => sl._id === s._id),
      );
      setValue("shipmentId", validShipments || shipmentList);
    } else {
      setValue("shipmentId", shipmentList);
    }
  }, [shipmentList, setValue, codTransferDetails]);

  useEffect(() => {
    if (!codTransferDetails) return;
    if (!accounts || !entities || !vendorList || !bankNameOptions) return;

    const accountObj =
      accounts?.find((acc) => acc._id === codTransferDetails.account) || null;
    const entityObj =
      entities?.find((ent) => ent._id === codTransferDetails.entity) || null;
    const vendorObj =
      vendorList?.find((v) => v._id === codTransferDetails.vendor?._id) || null;
    const bankNameObj =
      bankNameOptions?.find(
        (opt) =>
          opt._id ===
          (typeof codTransferDetails?.bankName === "object"
            ? codTransferDetails?.bankName?._id
            : codTransferDetails?.bankName),
      ) || null;
    const shipmentIds = (codTransferDetails.shipmentId || []).map((s) => ({
      _id: s._id,
      shipmentId: s.shipmentId,
      codAmount: s.codAmount || 0,
      chargeAmount: s.chargeAmount || 0,
    }));

    reset({
      account: accountObj,
      entity: entityObj,
      vendor: vendorObj,
      shipmentId: shipmentIds,
      transferMode: codTransferDetails.transferMode || "",
      bankName: bankNameObj ? bankNameObj._id : "",
      actualAmount: codTransferDetails.actualAmount?.toString() || "",
      transferAmount: codTransferDetails.transferAmount?.toString() || "",
      receiver: codTransferDetails.receiver || "",
      notes: codTransferDetails.notes || "",
      isActive: codTransferDetails.isActive ?? true,
      ticket:
        codTransferDetails.ticket?._id || codTransferDetails.ticket || null,
      deductChargeFromCOD: true, // UI only, always reset to true
    });
  }, [
    codTransferDetails,
    accounts,
    entities,
    vendorList,
    bankNameOptions,
    reset,
  ]);

  // Calculate amounts
  useEffect(() => {
    if (_watchShipmentId && _watchShipmentId.length > 0) {
      const feeAmount = +(totalCOD * (serviceChargePercentage / 100)).toFixed(
        2,
      );

      const transferAmount = _watchDeductCharge
        ? Math.max(totalCOD - feeAmount - totalCharge, 0)
        : totalCOD - feeAmount;

      setValue("actualAmount", totalCOD.toString());
      setValue("feeAmount", feeAmount.toString());
      setValue("transferAmount", transferAmount.toString());
    } else {
      setValue("actualAmount", "");
      setValue("feeAmount", "");
      setValue("transferAmount", "");
    }
  }, [
    _watchShipmentId,
    _watchDeductCharge,
    totalCOD,
    totalCharge,
    serviceChargePercentage,
    setValue,
  ]);

  useEffect(() => {
    if (selectedCollectionMethod !== "Bank") {
      setValue("bankName", "");
    }
  }, [selectedCollectionMethod, setValue]);

  const handleTransfer = (data) => {
    if (data.deductChargeFromCOD && totalCharge > totalCOD) {
      toast.error(
        `Cannot transfer! Delivery charge (Rs ${totalCharge.toLocaleString()}) exceeds COD amount (Rs ${totalCOD.toLocaleString()}). Vendor owes Rs ${(totalCharge - totalCOD).toLocaleString()} more than the COD collected.`
      );
      return;
    }

    const actualAmount = Number(data.actualAmount);
    const transferAmount = Number(data.transferAmount);

    // strip UI-only field before sending to backend
    const { deductChargeFromCOD, ...rest } = data;

    const payload = {
      ...rest,
      account: data?.account?._id || null,
      entity: data?.entity?._id || null,
      vendor: data?.vendor?._id || null,
      shipmentId: data?.shipmentId?.map((s) => s._id) || [],
      actualAmount,
      transferAmount,
      receiver: data?.receiver || null,
      bankName:
        selectedCollectionMethod?.name === "Bank" ? data.bankName : null,
      notes: data?.notes || null,
      ticket: data?.ticket || null,
      deductChargeFromCOD: deductChargeFromCOD ? totalCharge : 0,
    };
    onSubmit(payload);
  };

  return (
    <AppForm
      loading={
        isBusy ||
        loadingBankNameOptions ||
        loadingAccounts ||
        loadingEntities ||
        loadingVendors ||
        fetchingVendors ||
        loadingShipments ||
        fetchingShipments ||
        loadingTicket ||
        fetchingTicket
      }
    >
      <AppForm.Content>
        <Grid container spacing={4}>
          {/* Account — hidden, still in form state */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6} sx={{ display: "none" }}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                      setValue("vendor", null);
                      setValue("shipmentId", []);
                      setVendorTicket(null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={!!codTransferDetails || isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}

          {/* Entity — hidden, still in form state */}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6} sx={{ display: "none" }}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    required
                    label="Entity"
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("vendor", null);
                      setValue("shipmentId", []);
                      setVendorTicket(null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    disabled={!!codTransferDetails}
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          {/* Vendor */}
          <Grid item md={6}>
            <Controller
              name="vendor"
              control={control}
              rules={{ required: "Please select a Vendor Profile." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  required
                  label="Vendor Profile"
                  value={field.value ?? null}
                  onChange={(_, data) => {
                    field.onChange(data);
                    setValue("shipmentId", []);
                    setVendorTicket(null);
                  }}
                  getOptionLabel={(option) =>
                    option ? `${option?.ownerName} - ${option?.shopName}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={vendorList || []}
                  loading={loadingVendors || fetchingVendors}
                  loadingText="Loading Vendor Profiles..."
                  helperText={errors?.vendor?.message}
                  error={!!errors?.vendor}
                />
              )}
            />
          </Grid>

          {/* Shipments */}
          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select at least one shipment." }}
              render={({ field }) => (
                <BaseAutocomplete
                  {...field}
                  fullWidth
                  multiple
                  required
                  label="Shipments"
                  value={field.value || []}
                  onChange={(_, data) => field.onChange(data)}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={shipmentList || []}
                  loading={loadingShipments || fetchingShipments}
                  loadingText="Loading Shipments..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                />
              )}
            />
          </Grid>

          {/* Transfer Mode + Deduct checkbox inline on same row */}
          <Grid item md={6}>
            <Controller
              name="transferMode"
              control={control}
              rules={{ required: "Please select a Transfer Mode." }}
              render={({ field }) => (
                <BaseSelect
                  fullWidth
                  label="Transfer Mode"
                  options={_mappedAccountTypeList}
                  getOptionLabel={(opt) => opt.label || ""}
                  getOptionValue={(opt) => opt.value || ""}
                  onChange={(e) => setValue("transferMode", e.target.value)}
                  error={!!errors.transferMode}
                  helperText={errors.transferMode?.message}
                />
              )}
            />
          </Grid>

          <Grid item md={6} sx={{ display: "flex", alignItems: "center" }}>
            <Controller
              name="deductChargeFromCOD"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Deduct Charge from COD Amount?"
                />
              )}
            />
          </Grid>

          {/* Warning — charge exceeds COD */}
          {_watchDeductCharge &&
            totalCharge > totalCOD &&
            _watchShipmentId?.length > 0 && (
              <Grid item md={12}>
                <Alert severity="error">
                  Cannot transfer! Delivery charge (Rs {totalCharge.toLocaleString()}) exceeds COD amount (Rs {totalCOD.toLocaleString()}). Vendor owes Rs {(totalCharge - totalCOD).toLocaleString()} more than the COD collected.
                </Alert>
              </Grid>
            )}

          {/* Info banner — only when checkbox checked and charges exist */}
          {_watchDeductCharge &&
            totalCharge > 0 &&
            totalCharge <= totalCOD &&
            _watchShipmentId?.length > 0 && (
              <Grid item md={12}>
                <Alert severity="info">
                  Service charge of <strong>{serviceChargePercentage}%</strong>{" "}
                  (
                  <strong>
                    Rs {(totalCOD * (serviceChargePercentage / 100)).toFixed(2)}
                  </strong>
                  ) will be deducted from COD amount first, then delivery charge
                  of <strong>Rs {totalCharge.toLocaleString()}</strong> will be
                  deducted from the remaining COD amount.
                </Alert>
              </Grid>
            )}

          {/* Bank Name */}
          {selectedCollectionMethod?.name === "Bank" && (
            <Grid item md={6}>
              <Controller
                name="bankName"
                control={control}
                rules={{
                  required:
                    selectedCollectionMethod === "Bank"
                      ? "Please select a Bank Name."
                      : false,
                }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Bank Name"
                    required
                    value={
                      bankNameOptions?.find((opt) => opt._id === field.value) ||
                      null
                    }
                    onChange={(_, data) =>
                      setValue("bankName", data?._id || "")
                    }
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={bankNameOptions || []}
                    loading={loadingBankNameOptions}
                    loadingText="Loading Bank Names..."
                    error={!!errors.bankName}
                    helperText={errors.bankName?.message}
                  />
                )}
              />
            </Grid>
          )}

          {/* Actual Amount */}
          <Grid item md={6}>
            <Controller
              name="actualAmount"
              control={control}
              rules={{
                required: "Actual Amount is required.",
                min: { value: 0, message: "Amount must be non-negative." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Actual COD Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.actualAmount}
                  helperText={errors?.actualAmount?.message}
                  disabled
                />
              )}
            />
          </Grid>

          {/* Service Amount */}
          <Grid item md={6}>
            <Controller
              name="feeAmount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Service Amount (${serviceChargePercentage}%)`}
                  fullWidth
                  size="small"
                  disabled
                />
              )}
            />
          </Grid>

          {/* Transfer Amount */}
          <Grid item md={6}>
            <Controller
              name="transferAmount"
              control={control}
              rules={{
                required: "Transfer Amount is required.",
                min: { value: 0, message: "Amount must be non-negative." },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Transfer Amount"
                  fullWidth
                  size="small"
                  required
                  type="number"
                  error={!!errors?.transferAmount}
                  helperText={errors?.transferAmount?.message}
                  disabled
                />
              )}
            />
          </Grid>

          {/* Receiver */}
          <Grid item md={6}>
            <Controller
              name="receiver"
              control={control}
              rules={{ required: "Receiver is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Receiver"
                  fullWidth
                  size="small"
                  required
                  error={!!errors?.receiver}
                  helperText={errors?.receiver?.message}
                  placeholder="Enter receiver name"
                />
              )}
            />
          </Grid>

          {/* Notes */}
          <Grid item md={6}>
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  size="small"
                  multiline
                  rows={4}
                  error={!!errors?.notes}
                  helperText={errors?.notes?.message}
                />
              )}
            />
          </Grid>

          {/* Vendor Ticket */}
          {vendorTicket && (
            <Grid item md={6}>
              <Alert
                severity="info"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& .MuiAlert-message": { width: "100%" },
                }}
              >
                <Box
                  onClick={() =>
                    navigate(
                      `/app/crm/ticket/${vendorTicket._id}/detail?prevTab=${vendorTicket.stage}`,
                    )
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  <strong>COD Request Ticket No:</strong>
                  <Chip
                    label={vendorTicket.ticketNo || vendorTicket.ticketId}
                    color="primary"
                    size="small"
                  />
                  <Chip
                    label={vendorTicket?.stage}
                    color="warning"
                    size="small"
                  />
                  {vendorTicket.createdAt && (
                    <span style={{ fontSize: "0.875rem", color: "#666" }}>
                      Created:{" "}
                      {new Date(vendorTicket.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </Box>
              </Alert>
            </Grid>
          )}

          {/* Is Active */}
          {codTransferDetails && (
            <Grid item md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>

      <AppForm.Actions>
        <Button disabled={isBusy} onClick={() => navigate(-1)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isBusy}
          onClick={handleSubmit(handleTransfer)}
        >
          {codTransferDetails ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default CodTransferForm;
